var _react_refresh_temp_1, _react_refresh_temp_2;
var _react_refresh_temp_3;
_react_refresh_temp_3 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { store, useAppSelector } from '../../../store';
import RecordingModal from './recording/recordingModal';
import { RecordingEvent, RecordingType } from './recording/IRecording';
import useLocalRecording from './recording/useLocalRecording';
import useCloudRecording from './recording/useCloudRecording';
const isRecordingSelector = createSelector(_react_refresh_temp_1 = (state) => state.session, (session) => session.isActiveRecording);
$RefreshReg$(_react_refresh_temp_1, "isRecordingSelector$createSelector");
const RecordingIcon = ({ currentRoom }) => {
    var _a, _b, _c, _d;
    _react_refresh_temp_3();
    const showTooltip = store.getState().session.userDeviceType === 'desktop';
    const { hasError: localRecordingError, recordingEvent: localRecordingEvent, startRecording: startLocalRecording, stopRecording: stopLocalRecording, resetError: resetLocalRecordingError, } = useLocalRecording(currentRoom.localParticipant, currentRoom.name);
    const { hasError: hasCloudRecordingError, resetError: resetCloudRecordingError, startRecording: startCloudRecording, stopRecording: stopCloudRecording, } = useCloudRecording(currentRoom.sid);
    const { t } = useTranslation();
    const roomMetadata = store.getState().session.currentRoom
        .metadata;
    const isAllowRecording = roomMetadata.room_features.recording_features.is_allow;
    const isAdmin = (_b = (_a = store.getState().session.currentUser) === null || _a === void 0 ? void 0 : _a.metadata) === null || _b === void 0 ? void 0 : _b.is_admin;
    const isPresenter = (_d = (_c = store.getState().session.currentUser) === null || _c === void 0 ? void 0 : _c.metadata) === null || _d === void 0 ? void 0 : _d.is_presenter;
    const isRunningCloudRecording = useAppSelector(isRecordingSelector);
    const [disable, setDisable] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [allowRecording, setAllowRecording] = useState(true);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingType, setRecordingType] = useState(RecordingType.RECORDING_TYPE_NONE);
    const [timer, setTimer] = useState(undefined);
    const [checkedAutoRecording, setCheckedAutoRecording] = useState(false);
    useEffect(() => {
        if (isRunningCloudRecording &&
            recordingType !== RecordingType.RECORDING_TYPE_CLOUD) {
            if (recordingType === RecordingType.RECORDING_TYPE_LOCAL && isRecording) {
                stopLocalRecording();
            }
            setRecordingType(RecordingType.RECORDING_TYPE_CLOUD);
            clearTimeout(timer);
            setDisable(false);
            setIsRecording(true);
        }
        else if (isRunningCloudRecording &&
            recordingType === RecordingType.RECORDING_TYPE_CLOUD &&
            !isRecording) {
            clearTimeout(timer);
            setDisable(false);
            setIsRecording(true);
        }
        else if (!isRunningCloudRecording &&
            recordingType === RecordingType.RECORDING_TYPE_CLOUD &&
            isRecording) {
            clearTimeout(timer);
            setDisable(false);
            setIsRecording(false);
            setRecordingType(RecordingType.RECORDING_TYPE_NONE);
        }
    }, [isRunningCloudRecording, recordingType, isRecording, timer]);
    useEffect(() => {
        if (!isAllowRecording) {
            setAllowRecording(false);
        }
    }, []);
    useEffect(() => {
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [timer]);
    useEffect(() => {
        if (localRecordingEvent === RecordingEvent.STARTED_RECORDING) {
            setDisable(false);
            setIsRecording(true);
        }
        else if (localRecordingEvent === RecordingEvent.STOPPED_RECORDING) {
            setDisable(false);
            setIsRecording(false);
        }
    }, [localRecordingEvent]);
    useEffect(() => {
        const reset = () => {
            setDisable(false);
            setIsRecording(false);
            if (timer) {
                clearTimeout(timer);
            }
        };
        if (hasCloudRecordingError) {
            reset();
            resetCloudRecordingError();
        }
        if (localRecordingError) {
            reset();
            resetLocalRecordingError();
        }
    }, [hasCloudRecordingError, localRecordingError]);
    const onClickRecordingBtn = () => {
        if (!isRecording) {
            setOpenModal(true);
        }
        else {
            setOpenModal(false);
            setDisable(true);
            if (recordingType === RecordingType.RECORDING_TYPE_LOCAL) {
                stopLocalRecording();
            }
            else if (recordingType === RecordingType.RECORDING_TYPE_CLOUD) {
                stopCloudRecording();
                const timer = setTimeout(() => {
                    setDisable(false);
                    toast(t('footer.notice.recording-did-not-stop'), {
                        toastId: 'recording-status',
                        type: 'error',
                    });
                }, 30000);
                setTimer(timer);
            }
        }
    };
    const startRecording = (recordingType) => {
        if (recordingType === RecordingType.RECORDING_TYPE_LOCAL) {
            setDisable(true);
            setRecordingType(recordingType);
            startLocalRecording();
        }
        else if (recordingType === RecordingType.RECORDING_TYPE_CLOUD) {
            setDisable(true);
            setRecordingType(recordingType);
            startCloudRecording();
            const timer = setTimeout(() => {
                setDisable(false);
                toast(t('footer.notice.recording-not-start'), {
                    toastId: 'recording-status',
                    type: 'error',
                });
            }, 30000);
            setTimer(timer);
        }
    };
    const onCloseModal = (recordingType) => {
        setOpenModal(false);
        startRecording(recordingType);
    };
    // for auto cloud recording
    useEffect(() => {
        let timeout;
        if (isAllowRecording &&
            isAdmin &&
            isPresenter &&
            !isRunningCloudRecording &&
            !checkedAutoRecording &&
            roomMetadata.room_features.recording_features.enable_auto_cloud_recording) {
            timeout = setTimeout(() => {
                startRecording(RecordingType.RECORDING_TYPE_CLOUD);
            }, 1000);
        }
        setCheckedAutoRecording(true);
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [isRunningCloudRecording]);
    const render = () => {
        return (_jsxs(_Fragment, { children: [openModal ? (_jsx(RecordingModal, { showModal: openModal, recordingFeatures: roomMetadata.room_features.recording_features, onCloseModal: (recordingType) => onCloseModal(recordingType) })) : null, _jsxs("button", { className: `${isRecording ? 'record' : ''} footer-icon h-[35px] lg:h-[40px] w-[35px] lg:w-[40px] overflow-hidden rounded-full bg-[#F2F2F2] dark:bg-darkSecondary2 hover:bg-[#ECF4FF] ltr:mr-3 lg:ltr:mr-6 rtl:ml-3 lg:rtl:ml-6 flex items-center justify-center cursor-pointer ${showTooltip ? 'has-tooltip' : ''}`, onClick: () => onClickRecordingBtn(), disabled: disable, children: [_jsx("span", { className: "tooltip !bottom-[62px]", children: isRecording
                                ? t('footer.icons.stop-recording')
                                : t('footer.icons.start-recording') }), _jsx("i", { className: "pnm-rec primaryColor dark:text-darkText text-[11px] lg:text-[12px] font-['Nunito Sans'] font-bold" })] })] }));
    };
    return _jsx(_Fragment, { children: allowRecording && isAdmin ? render() : null });
};
_react_refresh_temp_2 = RecordingIcon;
$RefreshReg$(_react_refresh_temp_2, "RecordingIcon");
_react_refresh_temp_3(RecordingIcon, "a1eaBB6FzZB4UJX3hK5FzzabsPo=", false, () => [useLocalRecording, useCloudRecording, useTranslation, useAppSelector]);
export default RecordingIcon;
